import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = '/api';

interface AddBrandProps {
  onBrandAdded?: () => void;
  onCancel?: () => void;
  brand?: Brand; // 可选的品牌对象，用于编辑模式
}

interface Brand {
  id: number;
  name: string;
  description?: string;
  brand_colors?: string;
  brand_style?: string;
  target_audience?: string;
  slogan?: string;
  logo_path?: string;
}

export default function AddBrand({ onBrandAdded, onCancel, brand }: AddBrandProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand_colors: ['#000000'],
    brand_style: '',
    target_audience: '',
    slogan: '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 编辑模式：初始化表单数据
  useEffect(() => {
    if (brand) {
      // 解析品牌颜色
      let colors = ['#000000'];
      if (brand.brand_colors) {
        try {
          colors = JSON.parse(brand.brand_colors);
        } catch (e) {
          console.error('解析品牌颜色失败:', e);
        }
      }

      setFormData({
        name: brand.name || '',
        description: brand.description || '',
        brand_colors: colors,
        brand_style: brand.brand_style || '',
        target_audience: brand.target_audience || '',
        slogan: brand.slogan || '',
      });

      // 如果有logo，设置预览
      if (brand.logo_path) {
        setLogoPreview(`/api/brands/${brand.id}/logo`);
      }
    }
  }, [brand]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (index: number, value: string) => {
    const newColors = [...formData.brand_colors];
    newColors[index] = value;
    setFormData(prev => ({ ...prev, brand_colors: newColors }));
  };

  const addColor = () => {
    if (formData.brand_colors.length < 5) {
      setFormData(prev => ({ ...prev, brand_colors: [...prev.brand_colors, '#000000'] }));
    }
  };

  const removeColor = (index: number) => {
    if (formData.brand_colors.length > 1) {
      const newColors = formData.brand_colors.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, brand_colors: newColors }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const preview = URL.createObjectURL(file);
      setLogoPreview(preview);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (brand) {
        // 编辑模式：更新品牌信息
        await axios.put(`${API_BASE_URL}/brands/${brand.id}`, {
          name: formData.name,
          description: formData.description,
          brand_colors: formData.brand_colors,
          brand_style: formData.brand_style,
          target_audience: formData.target_audience,
          slogan: formData.slogan,
        });

        if (logoFile) {
          // 如果上传了新Logo，上传Logo
          const logoFormData = new FormData();
          logoFormData.append('logo', logoFile);
          await axios.post(`${API_BASE_URL}/brands/${brand.id}/logo`, logoFormData);
        }
      } else {
        // 新建模式：添加品牌基本信息
        const brandResponse = await axios.post(`${API_BASE_URL}/brands`, {
          name: formData.name,
          description: formData.description,
          brand_colors: formData.brand_colors,
          brand_style: formData.brand_style,
          target_audience: formData.target_audience,
          slogan: formData.slogan,
        });

        if (brandResponse.data.success && logoFile) {
          // 如果有Logo，上传Logo
          const formData = new FormData();
          formData.append('logo', logoFile);
          await axios.post(`${API_BASE_URL}/brands/${brandResponse.data.brand_id}/logo`, formData);
        }
      }

      if (onBrandAdded) {
        onBrandAdded();
      }
    } catch (err) {
      setError(brand ? '更新品牌失败，请重试' : '添加品牌失败，请重试');
      console.error(brand ? '更新品牌失败:' : '添加品牌失败:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-brand-modal">
      <div className="add-brand-header">
        <h2>{brand ? '编辑品牌' : '添加新品牌'}</h2>
        <button onClick={onCancel} className="close-button">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="add-brand-form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>品牌名称 *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="例如：乐伴眠"
            required
          />
        </div>

        <div className="form-group">
          <label>品牌描述</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="描述品牌的定位和特色..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>品牌颜色 *</label>
          <div className="color-picker">
            {formData.brand_colors.map((color, index) => (
              <div key={index} className="color-input-row">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => handleColorChange(index, e.target.value)}
                />
                {formData.brand_colors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeColor(index)}
                    className="remove-color-button"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            {formData.brand_colors.length < 5 && (
              <button type="button" onClick={addColor} className="add-color-button">
                <Plus size={16} />
                添加颜色
              </button>
            )}
          </div>
          <small className="help-text">选择1-5种品牌主色调，第一个颜色将作为品牌圆圈显示的颜色</small>
        </div>

        <div className="form-group">
          <label>品牌风格</label>
          <input
            type="text"
            name="brand_style"
            value={formData.brand_style}
            onChange={handleInputChange}
            placeholder="例如：现代简约、新中式、极简奢华"
          />
        </div>

        <div className="form-group">
          <label>目标受众</label>
          <input
            type="text"
            name="target_audience"
            value={formData.target_audience}
            onChange={handleInputChange}
            placeholder="例如：年轻人群、商务人士、家庭用户"
          />
        </div>

        <div className="form-group">
          <label>口号词</label>
          <input
            type="text"
            name="slogan"
            value={formData.slogan}
            onChange={handleInputChange}
            placeholder="例如：用心守护每一个深度睡眠"
          />
          <small className="help-text">口号词会以最小号字体添加到海报适合的位置</small>
        </div>

        <div className="form-group">
          <label>品牌Logo</label>
          <div className="logo-upload">
            {logoPreview ? (
              <div className="logo-preview">
                <img src={logoPreview} alt="Logo预览" />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="remove-logo-button"
                >
                  <Trash2 size={16} />
                  移除Logo
                </button>
              </div>
            ) : (
              <label className="upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="file-input"
                />
                <Upload size={32} />
                <p>点击或拖拽上传Logo图片</p>
                <small>支持 PNG, JPG, GIF, WEBP 格式，最大 5MB</small>
              </label>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            取消
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !formData.name}
          >
            {isSubmitting ? (brand ? '更新中...' : '添加中...') : (brand ? '更新品牌' : '添加品牌')}
          </button>
        </div>
      </form>
    </div>
  );
}